import config from 'constants/config'
import { collectGooglePhone } from 'api/auth'

const PHONE_SCOPE = 'openid https://www.googleapis.com/auth/user.phonenumbers.read'

let pendingCollectPhone = false
let pendingGooglePhoneCollectionAvailable = false
let googlePhoneAccountId: string | undefined
let googlePhoneMemberId: string | undefined
export interface GooglePhonePreview {
  phone: string
  phonePrefix: string
  verificationToken: string
}

export function setPendingGoogleCollectPhone(collectPhone: boolean) {
  pendingCollectPhone = collectPhone
}

export function consumePendingGoogleCollectPhone(): boolean {
  const value = pendingCollectPhone
  pendingCollectPhone = false
  return value
}

export function setGooglePhoneCollectionAvailable(available: boolean, accountId?: string, memberId?: string) {
  pendingGooglePhoneCollectionAvailable = available
  googlePhoneAccountId = available ? accountId : undefined
  googlePhoneMemberId = available ? memberId : undefined
}

export function getGooglePhoneAccountId(): string | undefined {
  return googlePhoneAccountId
}

export function getGooglePhoneCollectionAvailable(memberId?: string): boolean {
  return pendingGooglePhoneCollectionAvailable && googlePhoneMemberId === memberId
}

export function consumeGooglePhoneCollectionAvailable(): boolean {
  const value = pendingGooglePhoneCollectionAvailable
  pendingGooglePhoneCollectionAvailable = false
  return value
}

export function shouldCollectGooglePhoneAfterLogin({
  collectPhone,
  isSignUp,
  phone,
}: {
  collectPhone?: boolean
  isSignUp?: boolean
  phone?: string | null
}): boolean {
  return Boolean(collectPhone && isSignUp && !phone)
}

export type GooglePhoneFailure = 'unavailable' | 'cancelled' | 'popup_blocked' | 'account_mismatch' | 'failed'
export type GooglePhoneResult = { outcome: 'preview', preview: GooglePhonePreview } | { outcome: GooglePhoneFailure }

export function requestGooglePhone(accountHint: string): Promise<GooglePhoneResult> {
  return new Promise((resolve) => {
    const oauth2 = window.google?.accounts?.oauth2
    if (!oauth2) {
      resolve({ outcome: 'failed' })
      return
    }

    try {
      const client = oauth2.initCodeClient({
        client_id: config.GOOGLE_APP_ID,
        scope: PHONE_SCOPE,
        ux_mode: 'popup',
        login_hint: accountHint,
        select_account: false,
        error_callback: (error) => {
          resolve({ outcome: error.type === 'popup_closed' ? 'cancelled' : error.type === 'popup_failed_to_open' ? 'popup_blocked' : 'failed' })
        },
        callback: async(response) => {
          try {
            if (response.error) {
              resolve({ outcome: response.error === 'access_denied' ? 'cancelled' : 'failed' })
              return
            }
            if (response.code) {
              const preview = await collectGooglePhone(response.code)
              if (preview.outcome === 'preview' && preview.phone && preview.phone_prefix && preview.verification_token) {
                resolve({
                  outcome: 'preview',
                  preview: {
                    phone: preview.phone,
                    phonePrefix: preview.phone_prefix,
                    verificationToken: preview.verification_token,
                  },
                })
                return
              }
              resolve({ outcome: preview.outcome === 'unavailable' ? 'unavailable' : 'failed' })
              return
            }
            resolve({ outcome: 'failed' })
          } catch(error) {
            const failure = error as { code?: string, message?: string }
            resolve({ outcome: (failure?.code ?? failure?.message) === 'google_account_mismatch' ? 'account_mismatch' : 'failed' })
          }
        },
      })

      client.requestCode()
    } catch {
      resolve({ outcome: 'failed' })
    }
  })
}
